import React from 'react';
import { Box, Typography } from '@mui/material';
import OpenCareBrand from './OpenCareBrand';
import ThemeModeToggle from './ThemeModeToggle';
import { APP_NAME, APP_BUILD_NUMBER } from '../config/appInfo';
import { useThemeMode } from '../theme/ThemeModeContext';
import { accent, darkTokens, lightTokens } from '../theme/tokens';

export const AUTH_LOGIN_BACKGROUND = `${process.env.PUBLIC_URL}/images/Bakgrunn_Logon_Opencare.png`;

const AuthLandingLayout = ({
  children,
  tagline = 'Lås- og tilgangsadministrasjon for kommunal omsorg',
  showBackgroundImage = false
}) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const tokens = isDark ? darkTokens : lightTokens;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: showBackgroundImage ? 'transparent' : tokens.bg,
        overflow: 'hidden'
      }}
    >
      {showBackgroundImage && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -16,
              backgroundImage: `url(${AUTH_LOGIN_BACKGROUND})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(3px)',
              transform: 'scale(1.03)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: isDark
                ? 'linear-gradient(135deg, rgba(11, 20, 31, 0.48) 0%, rgba(21, 34, 50, 0.38) 55%, rgba(11, 20, 31, 0.52) 100%)'
                : 'linear-gradient(135deg, rgba(246, 244, 238, 0.42) 0%, rgba(255, 255, 255, 0.48) 55%, rgba(246, 244, 238, 0.4) 100%)'
            }
          }}
        />
      )}

      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 2
        }}
      >
        <ThemeModeToggle />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            flex: { md: '0 0 42%' },
            maxWidth: { md: 520 },
            bgcolor: showBackgroundImage
              ? isDark
                ? 'rgba(6, 13, 20, 0.38)'
                : 'rgba(14, 40, 65, 0.45)'
              : tokens.panel,
            backdropFilter: showBackgroundImage ? 'blur(6px)' : 'none',
            WebkitBackdropFilter: showBackgroundImage ? 'blur(6px)' : 'none',
            color: tokens.onPanel,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 3, sm: 4, md: 6, lg: 8 },
            py: { xs: 5, md: 8 },
            pt: { xs: 7, md: 8 }
          }}
        >
          {!showBackgroundImage && (
            <>
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  width: 280,
                  height: 280,
                  borderRadius: '50%',
                  bgcolor: accent.main,
                  opacity: isDark ? 0.1 : 0.14,
                  top: -80,
                  right: -60
                }}
              />
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  border: `2px solid ${accent.main}`,
                  opacity: isDark ? 0.18 : 0.25,
                  bottom: 48,
                  left: -40
                }}
              />
            </>
          )}

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <OpenCareBrand onDark variant="h4" gutterBottom />
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                maxWidth: 320,
                lineHeight: 1.6,
                color: isDark ? tokens.ink70 : 'rgba(255, 255, 255, 0.82)'
              }}
            >
              {tagline}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, sm: 4 },
            py: { xs: 5, md: 6 },
            pt: { xs: 7, md: 6 },
            bgcolor: showBackgroundImage
              ? isDark
                ? 'rgba(11, 20, 31, 0.12)'
                : 'rgba(255, 255, 255, 0.22)'
              : 'transparent',
            backdropFilter: showBackgroundImage ? 'blur(2px)' : 'none',
            WebkitBackdropFilter: showBackgroundImage ? 'blur(2px)' : 'none'
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 440 }}>{children}</Box>
        </Box>
      </Box>

      <Typography
        component="footer"
        variant="caption"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: 2,
          textAlign: 'center',
          color: tokens.ink70,
          letterSpacing: '0.02em',
          borderTop: `1px solid ${tokens.border}`,
          bgcolor: showBackgroundImage
            ? isDark
              ? 'rgba(11, 20, 31, 0.28)'
              : 'rgba(246, 244, 238, 0.35)'
            : tokens.bg
        }}
      >
        {APP_NAME} · Build {APP_BUILD_NUMBER}
      </Typography>
    </Box>
  );
};

export default AuthLandingLayout;
